import React, { useEffect, useState, useRef } from "react";
import { ArrowLeft, Brain, Activity, Cpu, Database, GitBranch, Gauge, TrendingUp, TrendingDown, Minus, Settings as SettingsIcon, LogOut, History, Layers, Compass, Blocks, Orbit } from "lucide-react";
import {
    fetchSgStatus,
    fetchSgSituation,
    fetchSgIntel,
    fetchSgTwins,
    fetchSgDecisions,
    updateSgConfig,
    fetchSgHypotheses,
    fetchSgRisk,
    fetchSgAutotune,
    fetchSgOnlineMl,
    fetchSgBandit,
    fetchSgSummary,
    fetchSgBotStatus,
    fetchSgRegimeLeaderboard,
    fetchSgActions,
    fetchSgEquityComparison,
    fetchSgAnalytics,
    fetchSgStrategyModules,
} from "@/lib/smartgrid_api";
import { RiskPanel, HypothesisPanel, OnlineMlPanel, AutoTunePanel, SummaryPanel, BotControlPanel, RegimeLeaderboardPanel, Sparkline } from "@/components/SmartGridAdvanced";
import { ActionFeedPanel, EquityComparePanel, AnalyticsPanel, GoLiveChecklist } from "@/components/SmartGridP3";
import GridLadder from "@/components/GridLadder";
import PriceChart from "@/components/PriceChart";
import { OpenOrdersTable, TradeHistoryTable } from "@/components/OrdersTables";
import SgOperationsPanel from "@/components/SmartGridOps";
import SmartGridStrategy from "@/components/SmartGridStrategy";
import { BinancePanel, GuardsPanel, PaperResetCard, MacroPanel } from "@/components/SmartGridBinance";
import TerminalLog from "@/components/TerminalLog";
import GridDetailDialog from "@/components/GridDetailDialog";
import SettingsDialog from "@/components/SettingsDialog";
import useTradeAlerts from "@/hooks/useTradeAlerts";
import { loadAlertPrefs } from "@/lib/tradeAlerts";
import { fetchPriceHistory } from "@/lib/api";
import { toast } from "sonner";
import {
    fetchSgBotGrid,
    fetchSgBotOrders,
    fetchSgBotTrades,
    fetchSgBotPriceHistory,
    fetchSgBotEvents,
    fetchSgGridDetail,
    fetchSgBotTelemetry,
    fetchSgBotConfig,
    updateSgBotConfig,
} from "@/lib/smartgrid_api";

const C = {
    bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6",
    sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B",
    bear: "#FF5C6C", side: "#A7B4C6", warn: "#FFB020", info: "#3DA7FF",
};

const HORIZONS = ["1", "5", "15", "30", "60"];

const REGIME_COLOR = (r) => {
    if (!r) return C.dim;
    if (r.includes("Bull")) return C.bull;
    if (r.includes("Bear") || r.includes("Crash") || r.includes("Capitul")) return C.bear;
    if (r.includes("Volatile") || r.includes("Liquidity")) return C.warn;
    return C.side;
};

const Pct = ({ v }) => <span>{Number(v ?? 0).toFixed(1)}%</span>;

const ProbBar = ({ bull, bear, side }) => (
    <div className="flex h-2 w-full overflow-hidden rounded-full" style={{ background: "#13202D" }}>
        <div style={{ width: `${bull}%`, background: C.bull }} />
        <div style={{ width: `${side}%`, background: C.side, opacity: 0.5 }} />
        <div style={{ width: `${bear}%`, background: C.bear }} />
    </div>
);

const MACRO_CHIP_COLOR = (r) => {
    if (!r) return C.dim;
    if (r.includes("Bull")) return C.bull;
    if (r.includes("Bear")) return C.bear;
    if (r === "Correction") return C.warn;
    return C.sub;
};

const RegimeBanner = ({ regime, status }) => {
    const color = REGIME_COLOR(regime?.regime);
    const macro = status?.macro;
    return (
        <div className="panel p-4 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="sg-regime-banner">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(46,230,214,0.12)", border: `1px solid ${C.accent}40` }}>
                    <Brain size={18} color={C.accent} />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.dim }}>Market Regime</div>
                    <div className="text-2xl font-semibold" style={{ color }} data-testid="sg-regime-name">
                        {regime?.regime || "—"}
                    </div>
                    {macro?.available && (
                        <div className="text-[10px] font-mono mt-0.5" data-testid="sg-macro-chip">
                            <span style={{ color: C.dim }}>macro: </span>
                            <span style={{ color: MACRO_CHIP_COLOR(macro.regime) }}>
                                {macro.regime} ({macro.score > 0 ? "+" : ""}{Number(macro.score ?? 0).toFixed(2)})
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.dim }}>Confidence</div>
                <div className="font-mono text-lg" style={{ color: C.text }}>{Number(regime?.confidence ?? 0).toFixed(0)}%</div>
            </div>
            <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.dim }}>
                    In Regime / Typical
                </div>
                <div className="font-mono text-lg" style={{ color: C.text }} data-testid="sg-regime-duration">
                    {regime?.elapsed_min ?? "—"}m
                    <span className="text-xs" style={{ color: C.dim }}>
                        {" "}/ ~{regime?.expected_duration_min ?? "—"}m
                        {regime?.duration_source === "learned" ? " · learned" : ""}
                    </span>
                </div>
            </div>
            <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.dim }}>BTC Price</div>
                <div className="font-mono text-lg" style={{ color: C.text }}>
                    ${Number(status?.price ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}
                </div>
            </div>
            <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.dim }}>Regime Hint (advisory)</div>
                <div className="text-sm" style={{ color: C.sub }}>{regime?.posture || "—"}</div>
            </div>
        </div>
    );
};

const POSTURE_COLOR = { aggressive: C.bull, neutral: C.side, defensive: C.warn, capitulation: C.bear };

// Situational awareness: what the AI believes, WHY, and what it is DOING
// about it (actuated posture + exit policy + churn health). Data:
// GET /api/smartgrid/situation.
const SituationCard = ({ situation }) => {
    if (!situation) return null;
    const posture = situation?.posture?.current || "neutral";
    const pColor = POSTURE_COLOR[posture] || C.side;
    const bear = situation?.macro_bear || {};
    const comps = (bear.components || []).slice(0, 4);
    const ep = situation?.bot?.exit_policy || {};
    const rec = situation?.bot?.reconciler || {};
    const runners = ep.runners || [];
    const armed = runners.filter((r) => r.armed).length;
    return (
        <div className="panel p-4 space-y-3" data-testid="sg-situation-card">
            <div className="flex flex-wrap items-center gap-3">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.dim }}>
                    AI Situational Awareness
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded font-mono uppercase"
                    style={{ color: pColor, background: `${pColor}1A`, border: `1px solid ${pColor}55` }}
                    data-testid="sg-posture-chip">
                    posture · {posture}
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded font-mono"
                    style={{ color: C.warn, background: "rgba(255,176,32,0.08)" }}>
                    macro-bear {(Number(bear.score ?? 0)).toFixed(2)}
                </span>
                {situation?.risk?.breaker_smoothed && (
                    <span className="text-[11px] px-2 py-0.5 rounded font-mono"
                        style={{ color: C.bear, background: "rgba(255,92,108,0.1)" }}>
                        BREAKER LATCHED
                    </span>
                )}
                {situation?.fall?.state === "knife" && (
                    <span className="text-[11px] px-2 py-0.5 rounded font-mono"
                        style={{ color: C.bear, background: "rgba(255,92,108,0.15)" }}
                        data-testid="sg-knife-chip">
                        🔪 KNIFE · crash {Number(situation.fall.crash_score ?? 0).toFixed(2)} · waiting for the end
                    </span>
                )}
                {situation?.fall?.state === "rebound" && (
                    <span className="text-[11px] px-2 py-0.5 rounded font-mono"
                        style={{ color: C.bull, background: "rgba(43,227,139,0.12)" }}
                        data-testid="sg-rebound-chip">
                        knife END — catch armed
                    </span>
                )}
                {situation?.run_active && (
                    <span className="text-[11px] px-2 py-0.5 rounded font-mono"
                        style={{ color: C.bull, background: "rgba(43,227,139,0.12)" }}
                        data-testid="sg-run-chip">
                        🏃 RUN · trailing all fills
                    </span>
                )}
                <span className="text-[11px] px-2 py-0.5 rounded font-mono"
                    style={{ color: C.bull, background: "rgba(43,227,139,0.08)" }}
                    data-testid="sg-exit-policy-chip">
                    {ep.profit_only !== false ? "PROFIT-ONLY EXITS" : "loss exits enabled"}
                </span>
            </div>
            <div className="text-sm" style={{ color: C.text }} data-testid="sg-situation-headline">
                {situation.headline}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Exit Policy (live)</div>
                    <div className="font-mono" style={{ color: C.text }}>
                        {Number(ep.effective_tp_mult ?? 1).toFixed(2)}x TP
                        {Number(ep.learned_tp_mult ?? 1) > 1 && Number(ep.effective_tp_mult ?? 1) === 1
                            ? ` (learned ${Number(ep.learned_tp_mult).toFixed(2)}x armed for aggressive)` : ""}
                    </div>
                    <div style={{ color: C.sub }}>runner: {ep.runner_mode || "off"}
                        {runners.length ? ` · ${runners.length} live / ${armed} armed` : ""}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Why (top signals)</div>
                    {comps.length
                        ? comps.map((c, i) => (
                            <div key={i} style={{ color: C.sub }} className="font-mono text-[11px]">
                                +{Number(c.points).toFixed(2)} {c.signal}
                            </div>))
                        : <div style={{ color: C.sub }}>no bearish signals</div>}
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Order Churn Health</div>
                    <div className="font-mono" style={{ color: C.text }}>
                        {rec.cancels_last_min ?? 0} cancels/min
                    </div>
                    <div style={{ color: C.sub }}>netted {rec.netted ?? 0} · deferred {rec.deferred_cancels ?? 0}
                        {rec.alarm_trips ? ` · ⚠ ${rec.alarm_trips} alarms` : ""}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Ladder Shape</div>
                    <div className="font-mono" style={{ color: C.text }}>
                        {Number(situation?.bot?.ladder?.spacing_progression ?? 1) > 1
                            ? `sparse ×${situation.bot.ladder.spacing_progression}` : "classic"}
                        {Number(situation?.bot?.ladder?.zone_cap ?? 0) > 0
                            ? ` · zone cap ${situation.bot.ladder.zone_cap}` : ""}
                    </div>
                    <div style={{ color: C.sub }}>
                        buys {situation?.bot?.active_buys ?? "—"} · holds {situation?.bot?.open_sells ?? "—"}
                        {situation?.bot?.pause_buys ? " · buys PAUSED" : ""}
                    </div>
                </div>
            </div>
            {runners.length > 0 && (
                <div className="text-[11px] font-mono" style={{ color: C.sub }} data-testid="sg-runner-list">
                    <div className="text-[10px] uppercase tracking-[0.12em] mb-1" style={{ color: C.dim }}>
                        Active stop-losses (every managed sell)
                    </div>
                    {runners.map((r) => (
                        <span key={r.grid_id} className="mr-4">
                            ▸ {r.grid_id?.slice(0, 6)} entry {r.entry} tp {r.tp}
                            {r.armed
                                ? <span style={{ color: C.bull }}> · SL {r.floor} (trailing, profit locked)</span>
                                : ` · SL pending — arms above ${r.l4_trigger}`}
                        </span>
                    ))}
                </div>
            )}
            {(situation?.bot?.zones || []).length > 0 && (
                <div className="text-[11px] font-mono" style={{ color: C.accent }} data-testid="sg-zone-list">
                    {(situation.bot.zones).map((z) => (
                        <span key={z.id} className="mr-4">
                            ◆ committed zone ${Number(z.price).toLocaleString("en-US")} · {Number(z.qty).toFixed(5)} BTC (pinned)
                        </span>
                    ))}
                    {situation?.bot?.grid_size_manual && (
                        <span style={{ color: C.warn }}>· grid: MANUAL (AI re-spacing off)</span>
                    )}
                </div>
            )}
        </div>
    );
};

const HorizonMatrix = ({ horizons }) => (
    <div className="panel" data-testid="sg-horizon-matrix">
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
            <Activity size={14} color={C.accent} />
            <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>Horizon Intelligence</span>
        </div>
        <div className="p-3 space-y-2">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.12em] px-1" style={{ color: C.dim }}>
                <div className="col-span-2">TF</div>
                <div className="col-span-5">Bull / Side / Bear</div>
                <div className="col-span-2 text-right">Bull</div>
                <div className="col-span-2 text-right">Bear</div>
                <div className="col-span-1 text-right">Conf</div>
            </div>
            {HORIZONS.map((h) => {
                const d = horizons?.[h] || {};
                return (
                    <div key={h} className="grid grid-cols-12 items-center gap-2 px-1 py-1.5 rounded hover:bg-[#121C28]"
                        data-testid={`sg-horizon-${h}`}>
                        <div className="col-span-2 font-mono text-sm" style={{ color: C.text }}>{h}m</div>
                        <div className="col-span-5"><ProbBar bull={d.bull || 0} bear={d.bear || 0} side={d.side || 0} /></div>
                        <div className="col-span-2 text-right font-mono text-xs" style={{ color: C.bull }}><Pct v={d.bull} /></div>
                        <div className="col-span-2 text-right font-mono text-xs" style={{ color: C.bear }}><Pct v={d.bear} /></div>
                        <div className="col-span-1 text-right font-mono text-xs" style={{ color: C.sub }}>
                            {Number(d.confidence ?? 0).toFixed(0)}
                            {d.calibration?.n >= 30 && (
                                <div className="text-[9px]" style={{ color: C.dim }} title={`Brier ${d.calibration.brier} over ${d.calibration.n} outcomes`}>
                                    hit {(d.calibration.hit_rate * 100).toFixed(0)}%
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

const Stat = ({ label, value, color = C.text }) => (
    <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: "#13202D" }}>
        <span className="text-[11px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>{label}</span>
        <span className="font-mono text-sm" style={{ color }}>{value}</span>
    </div>
);

const SignalPanel = ({ features, external }) => {
    const f = features || {};
    const fg = external?.fear_greed;
    const cg = external?.coingecko;
    return (
        <div className="panel" data-testid="sg-signal-panel">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
                <Cpu size={14} color={C.accent} />
                <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>Live Signals (math-only)</span>
            </div>
            <div className="p-4">
                <Stat label="Momentum 1m" value={`${((f.mom_1m || 0) * 100).toFixed(3)}%`} color={(f.mom_1m || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="Momentum 5m" value={`${((f.mom_5m || 0) * 100).toFixed(3)}%`} color={(f.mom_5m || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="Dist from VWAP" value={Number(f.dist_vwap ?? 0).toFixed(3)} color={(f.dist_vwap || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="Orderbook Imbalance" value={Number(f.ob_imbalance ?? 0).toFixed(3)} color={(f.ob_imbalance || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="Buy Pressure" value={`${((f.buy_pressure || 0) * 100).toFixed(1)}%`} color={(f.buy_pressure || 0) >= 0.5 ? C.bull : C.bear} />
                <Stat label="CVD Δ 5m" value={Number(f.cvd_slope_5m ?? 0).toFixed(3)} color={(f.cvd_slope_5m || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="Realized Vol (norm)" value={`${((f.rv_norm || 0) * 100).toFixed(0)} pct`} color={C.warn} />
                <Stat label="Spread" value={`${Number(f.spread_bps ?? 0).toFixed(2)} bps`} />
                <Stat label="VWAP" value={`$${Number(f.vwap ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`} />
                {fg && <Stat label="Fear & Greed" value={`${fg.value} · ${fg.classification}`} color={fg.value < 40 ? C.bear : fg.value > 60 ? C.bull : C.warn} />}
                {cg && <Stat label="BTC 24h" value={`${Number(cg.change_24h_pct ?? 0).toFixed(2)}%`} color={(cg.change_24h_pct || 0) >= 0 ? C.bull : C.bear} />}
                <div className="mt-2 text-[10px]" style={{ color: C.dim }}>
                    Data sufficiency: {((f.data_sufficiency || 0) * 100).toFixed(1)}%
                </div>
            </div>
        </div>
    );
};

const TwinLeaderboard = ({ twins }) => (
    <div className="panel" data-testid="sg-twin-leaderboard">
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
            <GitBranch size={14} color={C.accent} />
            <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>Digital Twin Leaderboard</span>
            <span className="ml-auto text-[10px]" style={{ color: C.dim }}>{twins.length} shadow bots</span>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sg-twin-table">
                <thead>
                    <tr className="text-left uppercase tracking-[0.1em] text-[10px]" style={{ color: C.dim }}>
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">Grid/TP/Trail/Rungs</th>
                        <th className="px-3 py-2">Equity</th>
                        <th className="px-3 py-2 text-right">Net PnL</th>
                        <th className="px-3 py-2 text-right">PF</th>
                        <th className="px-3 py-2 text-right">Sharpe</th>
                        <th className="px-3 py-2 text-right">Max DD</th>
                        <th className="px-3 py-2 text-right">Cycles</th>
                        <th className="px-3 py-2 text-right">Score</th>
                    </tr>
                </thead>
                <tbody>
                    {twins.length === 0 && (
                        <tr><td colSpan={9} className="px-3 py-6 text-center" style={{ color: "#3A4759" }}>Warming up…</td></tr>
                    )}
                    {twins.slice(0, 12).map((t) => {
                        const m = t.metrics || {}; const p = t.params || {};
                        return (
                            <tr key={t.id} className="border-t hover:bg-[#121C28]" style={{ borderColor: "#13202D" }} data-testid={`sg-twin-row-${t.rank}`}>
                                <td className="px-3 py-2 font-mono" style={{ color: t.rank === 1 ? C.accent : C.sub }}>{t.rank}</td>
                                <td className="px-3 py-2 font-mono" style={{ color: C.sub }}>${p.grid_size}/{p.tp_mult}x/{((p.trail ?? 0) * 100).toFixed(0)}%/{p.max_buys}</td>
                                <td className="px-3 py-1"><Sparkline data={t.equity_spark || []} /></td>
                                <td className="px-3 py-2 text-right font-mono" style={{ color: (m.net_pnl || 0) >= 0 ? C.bull : C.bear }}>${Number(m.net_pnl ?? 0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right font-mono" style={{ color: C.text }}>{Number(m.profit_factor ?? 0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right font-mono" style={{ color: C.text }}>{Number(m.sharpe ?? 0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right font-mono" style={{ color: C.bear }}>{Number(m.max_drawdown_pct ?? 0).toFixed(1)}%</td>
                                <td className="px-3 py-2 text-right font-mono" style={{ color: C.sub }}>{m.cycles ?? 0}</td>
                                <td className="px-3 py-2 text-right font-mono" style={{ color: C.accent }}>{Number(t.rank_score ?? 0).toFixed(1)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

const sevColor = (s) => (s === "good" ? C.bull : s === "warn" ? C.warn : C.info);

const DecisionFeed = ({ decision }) => {
    const recs = decision?.recommendations || [];
    const ctx = decision?.context || {};
    return (
        <div className="panel" data-testid="sg-decision-feed">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
                <Brain size={14} color={C.accent} />
                <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>AI Trade Manager — Advisory</span>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded" style={{ color: C.accent, background: "rgba(46,230,214,0.1)", border: `1px solid ${C.accent}30` }}>
                    band: {decision?.autonomy_band || "—"} · conf {Number(decision?.confidence ?? 0).toFixed(0)}%
                </span>
            </div>
            <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b" style={{ borderColor: C.border }}>
                <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Continuation</div><div className="font-mono text-sm" style={{ color: C.bull }}>{ctx.continuation_prob ?? 0}%</div></div>
                <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Adverse Risk</div><div className="font-mono text-sm" style={{ color: C.bear }}>{ctx.reversal_risk ?? 0}%</div></div>
                <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Vol Expansion</div><div className="font-mono text-sm" style={{ color: C.warn }}>{((ctx.vol_expansion ?? 0) * 100).toFixed(0)} pct</div></div>
                <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Risk Score</div><div className="font-mono text-sm" style={{ color: C.text }}>{ctx.risk_score ?? 0}</div></div>
            </div>
            <div className="p-3 space-y-2 max-h-[340px] overflow-y-auto">
                {recs.length === 0 && <div className="px-3 py-6 text-center" style={{ color: "#3A4759" }}>No recommendations yet.</div>}
                {recs.map((r, i) => (
                    <div key={`${r.action}-${r.target}-${i}`} className="rounded-md p-3" style={{ background: "#0B1219", border: `1px solid ${sevColor(r.severity)}30` }} data-testid="sg-rec-row">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-semibold" style={{ color: sevColor(r.severity) }}>{r.action}</span>
                            <span className="text-[11px]" style={{ color: C.dim }}>→ {r.target}</span>
                            <span className="ml-auto text-[9px] uppercase px-1.5 py-0.5 rounded" style={{ color: C.sub, background: "#13202D" }}>
                                needs: {r.requires_band}
                            </span>
                            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded"
                                style={{ color: r.would_apply_live ? C.bull : C.dim, background: "#13202D" }}>
                                {r.would_apply_live ? "in-band" : "below-band"} · advisory
                            </span>
                        </div>
                        <div className="text-xs mt-1.5" style={{ color: C.sub }}>{r.reason}</div>
                        <div className="text-[10px] mt-1" style={{ color: C.dim }}>{r.expected_ev}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AUTONOMY = [
    { key: "observe", label: "Observe" },
    { key: "suggest", label: "Suggest" },
    { key: "tp_trailing", label: "TP/Trail" },
    { key: "full", label: "Full" },
];

const AutonomySelector = ({ value, onChange }) => (
    <div className="flex items-center gap-2" data-testid="sg-autonomy-selector">
        <Gauge size={14} color={C.accent} />
        <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Autonomy ceiling</span>
        <div className="flex rounded-md overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
            {AUTONOMY.map((a) => (
                <button key={a.key} onClick={() => onChange(a.key)}
                    className="px-2.5 py-1 text-[11px] btn-anim"
                    style={{ background: value === a.key ? C.accent : "transparent", color: value === a.key ? C.bg : C.sub }}
                    data-testid={`sg-autonomy-${a.key}`}>
                    {a.label}
                </button>
            ))}
        </div>
    </div>
);

export default function SmartGridPage({ onBack, onLogout }) {
    const [status, setStatus] = useState(null);
    const [situation, setSituation] = useState(null);
    const [intel, setIntel] = useState(null);
    const [twins, setTwins] = useState([]);
    const [decision, setDecision] = useState(null);
    const [hypotheses, setHypotheses] = useState(null);
    const [risk, setRisk] = useState(null);
    const [autotune, setAutotune] = useState(null);
    const [onlineMl, setOnlineMl] = useState(null);
    const [bandit, setBandit] = useState(null);
    const [summary, setSummary] = useState(null);
    const [botStatus, setBotStatus] = useState(null);
    const [regimeLb, setRegimeLb] = useState(null);
    const [actions, setActions] = useState(null);
    const [equity, setEquity] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [botGrid, setBotGrid] = useState(null);
    const [priceHistory, setPriceHistory] = useState([]);
    const [botOrders, setBotOrders] = useState([]);
    const [botTrades, setBotTrades] = useState([]);
    const [botPriceHistory, setBotPriceHistory] = useState([]);
    const [botTelemetry, setBotTelemetry] = useState(null);
    const [botConfig, setBotConfig] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [botEvents, setBotEvents] = useState([]);
    const [gridDetailId, setGridDetailId] = useState(null);

    // Buy/sell fill alerts (vibration + sound), shared prefs across both bots.
    const alertPrefsRef = useRef(loadAlertPrefs());
    useTradeAlerts(botTrades, alertPrefsRef);
    const [gridDetailOpen, setGridDetailOpen] = useState(false);

    const onGridClick = (gridId) => {
        if (!gridId) return;
        setGridDetailId(gridId);
        setGridDetailOpen(true);
    };

    const refreshBotTelemetry = async () => {
        try { setBotTelemetry(await fetchSgBotTelemetry()); } catch (_) { /* ignore */ }
    };

    const openSettings = async () => {
        try { setBotConfig(await fetchSgBotConfig()); } catch (_) { /* ignore */ }
        setSettingsOpen(true);
    };

    const onSaveSettings = async (cfg) => {
        try {
            await updateSgBotConfig(cfg);
            setBotConfig(await fetchSgBotConfig());
            setSettingsOpen(false);
            toast.success("SmartGrid settings saved", { description: "Applies to the autonomous bot on the next tick." });
        } catch (e) {
            toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
        }
    };

    const refreshBot = async () => {
        try { setBotStatus(await fetchSgBotStatus()); } catch (_) { /* ignore */ }
    };

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [s, i, d] = await Promise.all([fetchSgStatus(), fetchSgIntel(), fetchSgDecisions()]);
                if (!alive) return;
                setStatus(s); setIntel(i); setDecision(d);
            } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 2000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [t, h, r, a, o, b] = await Promise.all([
                    fetchSgTwins(0), fetchSgHypotheses(), fetchSgRisk(),
                    fetchSgAutotune(), fetchSgOnlineMl(), fetchSgBandit(),
                ]);
                if (!alive) return;
                setTwins(t?.items || []); setHypotheses(h); setRisk(r);
                setAutotune(a); setOnlineMl(o); setBandit(b);
            } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 3000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => { fetchSgSummary().then(setSummary).catch(() => {}); }, []);
    // Registry-driven ladder palette: {order_kind: hex} from the Layer-3
    // strategy modules, so ladder hues always match the dashboard chips.
    const [strategyColors, setStrategyColors] = useState(null);
    useEffect(() => {
        fetchSgStrategyModules().then((res) => {
            const map = {};
            for (const m of res?.items || []) {
                for (const kind of m.order_kinds || []) map[kind] = m.color;
            }
            setStrategyColors(map);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [b, rl] = await Promise.all([fetchSgBotStatus(), fetchSgRegimeLeaderboard()]);
                if (!alive) return;
                setBotStatus(b); setRegimeLb(rl);
            } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 2500);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [ac, eq, an, sit] = await Promise.all([
                    fetchSgActions(), fetchSgEquityComparison(), fetchSgAnalytics(),
                    fetchSgSituation(),
                ]);
                if (!alive) return;
                setActions(ac); setEquity(eq); setAnalytics(an); setSituation(sit);
            } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 4000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [g, o, t, tel, ph, bph] = await Promise.all([
                    fetchSgBotGrid(), fetchSgBotOrders("open"),
                    fetchSgBotTrades(200), fetchSgBotTelemetry(),
                    fetchPriceHistory(),
                    fetchSgBotPriceHistory(),
                ]);
                if (!alive) return;
                setBotGrid(g); setBotOrders(o?.items || []);
                setBotTrades(t?.items || []); setBotTelemetry(tel);
                setPriceHistory(ph?.items || []);
                setBotPriceHistory(bph?.items || []);
            } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 2000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const e = await fetchSgBotEvents(200);
                if (!alive) return;
                setBotEvents((e?.items || []).slice().reverse());  // chronological for the log
            } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 2500);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const onAutonomy = async (k) => {
        try {
            await updateSgConfig({ autonomy_ceiling: k });
            setStatus((s) => ({ ...(s || {}), autonomy_ceiling: k }));
        } catch (_) { /* ignore */ }
    };

    // grid_ids whose position currently has an ACTIVE trailing stop-loss:
    // a runner whose trail has armed, or a structure past the breakeven arm.
    const ep = botStatus?.exit_policy || {};
    const trailingIds = new Set([
        ...(ep.runners || []).filter((r) => r.armed).map((r) => r.grid_id),
        ...(ep.structures || []).filter((s) => s.stage && s.stage !== "initial" && s.sl).map((s) => s.grid_id),
    ]);

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
            <div className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(11,15,20,0.85)", borderBottom: `1px solid ${C.border}` }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
                    <button onClick={onBack} className="btn-anim flex items-center gap-1.5 text-sm" style={{ color: C.sub }} data-testid="sg-back-button">
                        <ArrowLeft size={16} /> Dashboard
                    </button>
                    <div className="h-6 w-px" style={{ background: C.border }} />
                    <div className="flex items-center gap-2">
                        <Brain size={16} color={C.accent} />
                        <span className="text-sm font-semibold">SmartGrid AI Supervisor</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: C.accent, background: "rgba(46,230,214,0.1)" }}>v4 · leader-aware autonomous</span>
                    </div>
                    <div className="flex items-center gap-2 ml-1">
                        <span className="w-2 h-2 rounded-full" style={{ background: status?.feed_connected ? C.bull : C.bear }} />
                        <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                            {status?.feed_connected ? "Aster live" : "Aster down"}
                        </span>
                    </div>
                    <div className="flex items-center gap-2" data-testid="sg-binance-pill">
                        <span className="w-2 h-2 rounded-full" style={{ background: status?.binance?.connected ? C.bull : C.bear }} />
                        <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                            {status?.binance?.connected ? "Binance live" : "Binance down"}
                        </span>
                        {(status?.binance?.guard_active || status?.binance?.pump_guard_active) && (
                            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded font-mono"
                                style={{ color: status?.binance?.guard_active ? C.bear : C.warn, background: "#13202D" }}>
                                {status?.binance?.guard_active ? "guard" : "pump"} active
                            </span>
                        )}
                    </div>
                    <div className="flex-1" />
                    <a
                        href="#/smartgrid/docs"
                        className="btn-anim inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[11px]"
                        style={{ color: C.sub, border: `1px solid ${C.border}` }}
                        data-testid="sg-docs-link"
                    >
                        Docs
                    </a>
                    <a
                        href="#/smartgrid/history"
                        className="btn-anim inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[11px]"
                        style={{ color: C.sub, border: `1px solid ${C.border}` }}
                        data-testid="sg-history-link"
                    >
                        <History size={13} /> History
                    </a>
                    <a
                        href="#/smartgrid/group-sell"
                        className="btn-anim inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[11px]"
                        style={{ color: C.sub, border: `1px solid ${C.border}` }}
                        data-testid="sg-groupsell-link"
                    >
                        <Layers size={13} /> Group Sell
                    </a>
                        <a
                            href="#/smartgrid/data-lab"
                            className="btn-anim flex items-center gap-1.5 text-sm"
                            style={{ color: C.sub }}
                            data-testid="sg-data-lab-link"
                        >
                            <Database size={15} /> Data Lab
                        </a>
                        <a
                            href="#/smartgrid/blueprint"
                            className="btn-anim flex items-center gap-1.5 text-sm"
                            style={{ color: C.sub }}
                            data-testid="sg-blueprint-link"
                        >
                            <Compass size={15} /> Blueprint
                        </a>
                        <a
                            href="#/smartgrid/architecture"
                            className="btn-anim flex items-center gap-1.5 text-sm"
                            style={{ color: C.sub }}
                            data-testid="sg-architecture-link"
                        >
                            <Blocks size={15} /> Architecture
                        </a>
                        <a
                            href="#/smartgrid/map"
                            className="btn-anim flex items-center gap-1.5 text-sm"
                            style={{ color: C.sub }}
                            data-testid="sg-system-map-link"
                        >
                            <Orbit size={15} /> System Map
                        </a>
                    <AutonomySelector value={status?.autonomy_ceiling || "full"} onChange={onAutonomy} />
                    {onLogout && (
                        <button onClick={onLogout} className="btn-anim flex items-center gap-1.5 text-sm ml-1" style={{ color: C.sub }} data-testid="sg-logout-button">
                            <LogOut size={15} /> Logout
                        </button>
                    )}
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 space-y-4">
                <RegimeBanner regime={intel?.regime || status?.regime} status={status} />
                <SituationCard situation={situation} />
                <BotControlPanel status={botStatus} onChanged={refreshBot} />

                {/* ===== Autonomous SmartGrid bot — live trading operations ===== */}
                <div className="flex items-center gap-2 pt-1">
                    <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: C.sub }}>
                        SmartGrid Bot — Live Operations
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                        style={{ color: C.accent, background: "rgba(46,230,214,0.1)" }}>
                        sgb-* · isolated
                    </span>
                    <div className="flex-1 h-px" style={{ background: C.border }} />
                    <button
                        onClick={openSettings}
                        className="btn-anim flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px]"
                        style={{ color: C.sub, border: `1px solid ${C.border}` }}
                        data-testid="sg-settings-button"
                    >
                        <SettingsIcon size={13} /> Settings
                    </button>
                </div>
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-7">
                        <GridLadder
                            price={botGrid?.last_price || botStatus?.last_price || null}
                            gridSize={botGrid?.grid_size || 250}
                            buys={botGrid?.active_buys || []}
                            sells={botGrid?.active_sells || []}
                            mode={botStatus?.mode || "paper"}
                            symbol={botGrid?.symbol || "BTCUSDT"}
                            trailingIds={trailingIds}
                            strategyColors={strategyColors}
                        />
                    </div>
                    <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
                        <SgOperationsPanel telemetry={botTelemetry} onRefresh={refreshBotTelemetry} />
                        <PriceChart
                            data={priceHistory}
                            buys={botGrid?.active_buys || []}
                            sells={botGrid?.active_sells || []}
                            symbol={botGrid?.symbol || "BTCUSDT"}
                        />
                    </div>
                    <div className="col-span-12">
                        <SmartGridStrategy />
                    </div>
                    <div className="col-span-12">
                        <PriceChart
                            data={botPriceHistory}
                            buys={botGrid?.active_buys || []}
                            sells={botGrid?.active_sells || []}
                            label="SmartGrid · Live Price"
                        />
                    </div>
                    <div className="col-span-12 lg:col-span-4">
                        <BinancePanel />
                    </div>
                    <div className="col-span-12 lg:col-span-4">
                        <GuardsPanel />
                    </div>
                    <div className="col-span-12 lg:col-span-4">
                        <PaperResetCard botStatus={botStatus} onDone={refreshBot} />
                    </div>
                    <div className="col-span-12 lg:col-span-6">
                        <OpenOrdersTable orders={botOrders} onGridClick={onGridClick} />
                    </div>
                    <div className="col-span-12 lg:col-span-6">
                        <TradeHistoryTable trades={botTrades} onGridClick={onGridClick} />
                    </div>
                    <div className="col-span-12">
                        <TerminalLog events={botEvents} />
                    </div>
                </div>

                <EquityComparePanel equity={equity} />
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-6"><ActionFeedPanel actions={actions} /></div>
                    <div className="col-span-12 lg:col-span-3"><GoLiveChecklist status={status} botStatus={botStatus} autotune={autotune} risk={risk} /></div>
                    <div className="col-span-12 lg:col-span-3"><AnalyticsPanel analytics={analytics} /></div>
                    <div className="col-span-12 lg:col-span-7"><MacroPanel /></div>
                    <div className="col-span-12 lg:col-span-5"><HorizonMatrix horizons={intel?.horizons} /></div>
                    <div className="col-span-12 lg:col-span-5"><SignalPanel features={intel?.features} external={intel?.external} /></div>
                    <div className="col-span-12 lg:col-span-7"><DecisionFeed decision={decision} /></div>
                    <div className="col-span-12"><TwinLeaderboard twins={twins} /></div>
                    <div className="col-span-12 lg:col-span-4"><RiskPanel risk={risk} /></div>
                    <div className="col-span-12 lg:col-span-4"><OnlineMlPanel onlineMl={onlineMl} bandit={bandit} meta={status?.meta_controller} generation={status?.twin_generation} /></div>
                    <div className="col-span-12 lg:col-span-4"><AutoTunePanel autotune={autotune} autonomy={status?.autonomy_ceiling || "full"} /></div>
                    <div className="col-span-12 lg:col-span-6"><HypothesisPanel hypotheses={hypotheses} /></div>
                    <div className="col-span-12 lg:col-span-6"><RegimeLeaderboardPanel regimeLeaderboard={regimeLb} /></div>
                    <div className="col-span-12"><SummaryPanel summary={summary} onGenerated={setSummary} /></div>
                </div>
                <div className="py-4 text-center text-[10px] uppercase tracking-[0.18em]" style={{ color: "#3A4759" }}>
                    SmartGrid AI supervises its own isolated bot · Binance leader-venue guards + cross-exchange intel · GridDesk is never touched
                </div>
            </div>
            <SettingsDialog
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                initial={botConfig || {}}
                autoSizing={botConfig?.auto_sizing || null}
                walletAllocation={botStatus?.wallet_allocation || null}
                onSave={onSaveSettings}
                bot="smartgrid"
                isRunning={!!botStatus?.running}
                onAlertPrefsChange={(p) => { alertPrefsRef.current = p; }}
                hideGridSize
            />
            <GridDetailDialog
                gridId={gridDetailId}
                open={gridDetailOpen}
                onOpenChange={setGridDetailOpen}
                fetcher={fetchSgGridDetail}
            />
        </div>
    );
}
